import React, {
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Header,
	Icon,
	Modal,
	Progress,
	Segment
} from 'semantic-ui-react';

import useForm from '../../hooks/useForm';

import styles from './dd-file-upload-modal.module.css';
import { handleError } from '../../services/utils';

const DDFileUploadModal = ( {
	allowMultiple,
	closeModal,
	open,
	onUpload,
	upload
} ) => {
	const dropRef = useRef( null );
	const inputRef = useRef( null );

	const [ drag, setDrag ] = useState( false );
	const [ inProgress, setInProgress ] = useState( false );
	const [ uploadProgress, setUploadProgress ] = useState( {} );

	const {
		formData,
		updateField
	} = useForm( { files: [] } );

	const handleDrag = useCallback( e => {
		e.preventDefault();
		e.stopPropagation();
	}, [] );

	const handleDragIn = useCallback( e => {
		e.preventDefault();
		e.stopPropagation();

		if ( e.dataTransfer.items && e.dataTransfer.items.length > 0 ) {
			setDrag( true );
		}
	}, [] );

	const handleDragOut = useCallback( e => {
		e.preventDefault();
		e.stopPropagation();
		setDrag( false );
	}, [] );

	const handleDrop = useCallback( e => {
		e.preventDefault();
		e.stopPropagation();
		setDrag( false );

		allowMultiple
			? updateField( {
				target: {
					name: 'files',
					value: [ ...formData.files, ...e.dataTransfer.files ]
				}
			} )
			: updateField( {
				target: {
					name: 'files',
					value: [ e.dataTransfer.files[ 0 ] ]
				}
			} );
	}, [
		allowMultiple,
		formData.files,
		updateField
	] );

	useEffect( () => {
		let target = dropRef.current;

		if ( open && target ) {
			target.addEventListener( 'dragenter', handleDragIn );
			target.addEventListener( 'dragleave', handleDragOut );
			target.addEventListener( 'dragover', handleDrag );
			target.addEventListener( 'drop', handleDrop );

			return () => {
				target.removeEventListener( 'dragenter', handleDragIn );
				target.removeEventListener( 'dragleave', handleDragOut );
				target.removeEventListener( 'dragover', handleDrag );
				target.removeEventListener( 'drop', handleDrop );
			};
		}

		return () => null;
	}, [
		handleDrag,
		handleDragIn,
		handleDragOut,
		handleDrop,
		open
	] );

	const handleButtonUpload = useCallback( () => {
		inputRef.current.click();
	}, [] );

	const mapFiles = useCallback( () => [ ...formData.files ].map( ( file, index ) => {
		const handleDeleteFile = () => {
			const value = formData.files.reduce( ( acc, file, i ) => {
				if ( index === i ) {
					return acc;
				}

				return [ ...acc, file ];
			}, [] );

			updateField( { target: { name: 'files', value } } );
		};

		return (
			<Segment
				className={ styles.fileItem }
				inverted={ drag }
				key={ index }
				basic
			>
				{ uploadProgress[ index ]
					? <Progress
						indicating
						percent={ uploadProgress[ index ] }
						error={ uploadProgress[ index ] > 100 }
					/>
					: null
				}
				{ file.name }
				{ uploadProgress[ 0 ]
					? <Icon
						className={ styles.fileItemDeleteIcon }
						name={ uploadProgress[ index ] < 100
							? 'spinner'
							: uploadProgress[ index ] > 100
								? 'times circle'
								: 'check circle'
						}
						loading={ uploadProgress[ index ] < 100 }
						size="large"
					/>
					: <Icon
						className={ styles.fileItemDeleteIcon }
						link
						name="close"
						onClick={ handleDeleteFile }
						size="large"
					/>
				}
			</Segment>
		);
	} ), [
		drag,
		formData.files,
		updateField,
		uploadProgress
	] );

	const handleUpload = useCallback( async () => {
		setInProgress( true );

		const onUploadProgress = i => progress => {
			let percentCompleted = Math.round( progress.loaded * 100 / progress.total );

			setUploadProgress( prevState => ( { ...prevState, [ i ]: percentCompleted } ) );
		};

		for ( let i = 0; i < formData.files.length; i++ ) {
			const file = formData.files[ i ];

			const data = new FormData();

			data.append( file.name, file );

			try {
				const response = await upload( data, onUploadProgress( i ) );

				if ( onUpload ) {
					onUpload( response );
				}
			} catch ( error ) {
				handleError( error );
				setUploadProgress( prevState => ( {
					...prevState,
					[ i ]: 101
				} ) );
			}
		}

		setInProgress( false );
	}, [
		formData.files,
		onUpload,
		upload
	] );

	const handleClose = useCallback( () => {
		setUploadProgress( {} );
		updateField( {
			target: {
				name: 'files',
				value: []
			}
		} );
		closeModal();
	}, [
		closeModal,
		updateField
	] );

	const handleHiddenInputChange = useCallback(
		e => updateField( {
			target: {
				name: 'files',
				value: [
					...formData.files,
					...e.target.files
				]
			}
		} ),
		[
			formData.files,
			updateField
		]
	);

	return (
		<Modal
			onClose={ handleClose }
			open={ open }
			size="small"
		>
			<Modal.Header>Upload Files</Modal.Header>
			{	formData.files.length === 0
				? <div
					className={ styles.container }
				>
					<div
						className={ `${ styles.dropTarget } ${ drag ? styles.drag : null }` }
						ref={ dropRef }
					>
						<Icon.Group size="huge">
							<Icon
								name={ 'file outline' }
								inverted
							/>
							<Icon
								corner
								inverted
								name="add"
							/>
						</Icon.Group>
						<Header
							as="h2"
							inverted
						>
							Drop to Add File(s)
						</Header>
					</div>
					<Icon
						name="upload"
						size="huge"
					/>
					<Header as="h2">Drag Files Here to Upload</Header>
					<input
						hidden
						multiple={ allowMultiple }
						onChange={ handleHiddenInputChange }
						ref={ inputRef }
						type="file"
					/>
					<Button
						className={ styles.uploadButton }
						content="Browse Files..."
						inverted={ drag }
						onClick={ handleButtonUpload }
					/>
				</div>
				: <div className={ styles.fileViewContainer }>
					<div
						className={ `${ styles.dropTarget } ${ drag ? styles.drag : null }` }
						ref={ dropRef }
					>
						<Icon.Group
							size="huge"
						>
							<Icon
								name="file outline"
								inverted
							/>
							<Icon corner inverted name="add" />
						</Icon.Group>
						<Header as="h2" inverted>Drop to Add File(s)</Header>
					</div>
					{ !inProgress
						? <Segment
							basic
							className={ styles.fileItem }
						>
							<input
								hidden
								multiple={ allowMultiple }
								onChange={ handleHiddenInputChange }
								ref={ inputRef }
								type="file"
							/>
							<strong>
								<Button
									className={ styles.uploadButton }
									content="Browse Files..."
									onClick={ handleButtonUpload }
									size="tiny"
								/>
								&nbsp;&nbsp;or drag files to upload.
							</strong>
						</Segment>
						: null
					}
					{ mapFiles() }
				</div>
			}
			<Modal.Actions>
				<Button
					content="Close"
					disabled={ inProgress && Object.values( uploadProgress ).some( item => item < 100 ) }
					loading={ inProgress && Object.values( uploadProgress ).some( item => item < 100 ) }
					onClick={ handleClose }
					size="small"
				/>
				{ formData.files.length
					&& formData.files.some( ( file, i ) => uploadProgress[ i ] !== 100 )
					&& !inProgress
					? (
						<Button
							color="pink"
							content="Upload"
							onClick={ handleUpload }
							size="small"
						/>
					)
					: null
				}
			</Modal.Actions>
		</Modal>
	);
};

DDFileUploadModal.propTypes = {
	allowMultiple: PropTypes.bool,
	closeModal: PropTypes.func.isRequired,
	onUpload: PropTypes.func,
	open: PropTypes.bool,
	upload: PropTypes.func.isRequired
};

export default DDFileUploadModal;
